/**
 * Linear Progress Component
 */

import { v4 as uuidv4 } from 'uuid'

import { IBuilderComponent } from '../../base'
import { BaseAiComponentInput } from '../../registry'
import type {
  ColorAdaptiveValue,
  ColorVariant,
  GradientDirection,
  ResponsiveValue,
  SpacingInputValue,
  TypographyValue,
  VariableTextValue,
} from '../../types'
import {
  deepMerge,
  parseAlignmentFormat,
  parseColorFormat,
  parseResponsiveFormat,
  parseSpacingFormat,
  parseToVariableText,
  parseTypographyFormat,
  parseWidthFormat,
} from '../../parser-utils'
import {
  formatFromVariableText,
  formatFromVariableSelect,
  formatSpacingFormat,
  formatTypographyFormat,
  formatWidthFormat,
  formatColorFormat,
  formatResponsiveValue,
} from '../../format-utils'
import linearProgressTemplate from './linear-progress.template.json'

const LINEAR_PROGRESS_DEFAULT_TEMPLATE = linearProgressTemplate as ILinearProgressComponentSettings

export interface ILinearProgressComponentSettings {
  // Data settings
  'linear-progress-data-value': VariableTextValue | { flag: number; id: string; value: string }
  'linear-progress-data-max-value': { flag: number; id: string; value: string } | any
  'linear-progress-data-title': VariableTextValue

  // Design settings
  'linear-progress-design-scheme'?: any
  'linear-progress-design-comp-title-color'?: any
  'linear-progress-design-active-indicator-color'?: any
  'linear-progress-design-track-color'?: any
  'linear-progress-design-typography'?: ResponsiveValue<TypographyValue>
  'linear-progress-design-heading-typography'?: ResponsiveValue<TypographyValue>
  'linear-progress-design-value-typography'?: ResponsiveValue<TypographyValue>
  'linear-progress-design-width'?: ResponsiveValue<string[] | number[]>
  'linear-progress-design-height'?: ResponsiveValue<string[] | number[]>
  'linear-progress-design-alignment'?: ResponsiveValue<any>
  'linear-progress-design-heading-alignment'?: any
  'linear-progress-design-margin'?: ResponsiveValue<SpacingInputValue>
  'linear-progress-design-title-margin'?: ResponsiveValue<SpacingInputValue>
  'linear-progress-design-value-margin'?: SpacingInputValue
  'linear-progress-design-group-color'?: any
  'linear-progress-design-label-color'?: any
  'linear-progress-design-label-color-solid'?: any
  'linear-progress-design-label-color-start-gradient'?: any
  'linear-progress-design-label-color-end-gradient'?: any
  'linear-progress-design-label-color-gradient-direction'?: any
  'linear-progress-design-value-color'?: any
  'linear-progress-design-value-color-solid'?: ColorAdaptiveValue<ColorVariant>
  'linear-progress-design-value-color-start-gradient'?: ColorAdaptiveValue<ColorVariant>
  'linear-progress-design-value-color-end-gradient'?: ColorAdaptiveValue<ColorVariant>
  'linear-progress-design-value-color-gradient-direction'?: ColorAdaptiveValue<GradientDirection>
  'linear-progress-design-circle-color'?: any
  'linear-progress-design-circle-color-solid'?: any
  'linear-progress-design-circle-color-start-gradient'?: any
  'linear-progress-design-circle-color-end-gradient'?: any
  'linear-progress-design-circle-color-gradient-direction'?: any
  'linear-progress-design-track-color-solid'?: any
  'linear-progress-design-track-color-start-gradient'?: any
  'linear-progress-design-track-color-end-gradient'?: any
  'linear-progress-design-track-color-gradient-direction'?: any
}

export interface ILinearProgressComponent extends IBuilderComponent {
  componentId: 'linear-progress'
  groupId: 'progress'
  settings: ILinearProgressComponentSettings
}

export interface AiLinearProgressInput extends BaseAiComponentInput {
  value?: string | number
  maxValue?: string | number
  progressTitle?: string[] // Format: ["text:Progress"] or ["text:Loading ", "cr:itemName"]
  width?: string
  height?: string | number
  alignment?: string
  headingAlignment?: string
  margin?: string
  titleMargin?: string
  valueMargin?: string
  typography?: string
  headingTypography?: string
  valueTypography?: string
  trackColor?: string
  valueColor?: string
  circleColor?: string
}

export class LinearProgressComp {
  getTemplate(): ILinearProgressComponentSettings {
    return LINEAR_PROGRESS_DEFAULT_TEMPLATE
  }

  /**
   * format linear progress component settings to AI linear progress input
   */
  format(settings: ILinearProgressComponentSettings): AiLinearProgressInput {
    const result: AiLinearProgressInput = {
      type: 'linear-progress',
    }

    // Data - Value (from VariableSelectValue or VariableTextValue)
    if (settings['linear-progress-data-value']) {
      if ('flag' in settings['linear-progress-data-value'] && 'value' in settings['linear-progress-data-value']) {
        const value = formatFromVariableSelect(settings['linear-progress-data-value'] as any)
        if (value && value.startsWith('text:')) {
          const numValue = parseFloat(value.substring(5))
          result.value = isNaN(numValue) ? value.substring(5) : numValue
        } else {
          result.value = value
        }
      } else {
        const value = formatFromVariableText(settings['linear-progress-data-value'] as any)
        if (value.length > 0) {
          const numValue = parseFloat(value.join(' '))
          result.value = isNaN(numValue) ? value.join(' ') : numValue
        }
      }
    }

    // Data - Max Value
    if (settings['linear-progress-data-max-value'] && 'value' in settings['linear-progress-data-max-value']) {
      const maxValue = (settings['linear-progress-data-max-value'] as any).value
      const numValue = parseFloat(String(maxValue))
      result.maxValue = isNaN(numValue) ? maxValue : numValue
    }

    // Data - Title
    if (settings['linear-progress-data-title']) {
      result.progressTitle = formatFromVariableText(settings['linear-progress-data-title'])
    }

    // Design - Width
    if (settings['linear-progress-design-width']) {
      result.width = formatWidthFormat(settings['linear-progress-design-width'])
    }

    // Design - Height
    if (settings['linear-progress-design-height']) {
      const height = settings['linear-progress-design-height']
      const values = Object.values(height)
      if (values.length > 0) {
        const allSame = values.every((v) => {
          const arr = v as number[]
          return arr.length > 0 && arr[0] === (values[0] as number[])[0]
        })
        if (allSame && values[0]?.length > 0) {
          result.height = (values[0] as number[])[0]
        } else {
          result.height = formatResponsiveValue(height, (val) => {
            const arr = val as number[]
            return arr.length > 0 ? `${arr[0]}px` : '10px'
          })
        }
      }
    }

    // Design - Alignment
    if (settings['linear-progress-design-alignment']) {
      const alignment = settings['linear-progress-design-alignment']
      const values = Object.values(alignment)
      if (values.length > 0) {
        const allSame = values.every((v) => v === values[0])
        if (allSame) {
          result.alignment = values[0]
        } else {
          result.alignment = formatResponsiveValue(alignment, (val) => String(val))
        }
      }
    }

    // Design - Heading Alignment
    if (settings['linear-progress-design-heading-alignment']) {
      result.headingAlignment = settings['linear-progress-design-heading-alignment']
    }

    // Design - Margin
    if (settings['linear-progress-design-margin']) {
      result.margin = formatSpacingFormat(settings['linear-progress-design-margin'])
    }

    // Design - Title Margin
    if (settings['linear-progress-design-title-margin']) {
      result.titleMargin = formatSpacingFormat(settings['linear-progress-design-title-margin'])
    }

    // Design - Value Margin
    if (settings['linear-progress-design-value-margin']) {
      result.valueMargin = formatSpacingFormat({ phone: settings['linear-progress-design-value-margin'] } as any)
    }

    // Design - Typography
    if (settings['linear-progress-design-typography']) {
      result.typography = formatTypographyFormat(settings['linear-progress-design-typography'])
    }

    // Design - Heading Typography
    if (settings['linear-progress-design-heading-typography']) {
      result.headingTypography = formatTypographyFormat(settings['linear-progress-design-heading-typography'])
    }

    // Design - Value Typography
    if (settings['linear-progress-design-value-typography']) {
      result.valueTypography = formatTypographyFormat(settings['linear-progress-design-value-typography'])
    }

    // Design - Track Color
    if (settings['linear-progress-design-track-color']) {
      result.trackColor = formatColorFormat({
        colorType: settings['linear-progress-design-track-color'] as any,
        colorSolid: settings['linear-progress-design-track-color-solid'] as any,
        colorStartGradient: settings['linear-progress-design-track-color-start-gradient'] as any,
        colorEndGradient: settings['linear-progress-design-track-color-end-gradient'] as any,
        colorGradientDirection: settings['linear-progress-design-track-color-gradient-direction'] as any,
      })
    }

    // Design - Value Color
    if (settings['linear-progress-design-value-color']) {
      result.valueColor = formatColorFormat({
        colorType: settings['linear-progress-design-value-color'] as any,
        colorSolid: settings['linear-progress-design-value-color-solid'],
        colorStartGradient: settings['linear-progress-design-value-color-start-gradient'],
        colorEndGradient: settings['linear-progress-design-value-color-end-gradient'],
        colorGradientDirection: settings['linear-progress-design-value-color-gradient-direction'],
      })
    }

    // Design - Circle Color
    if (settings['linear-progress-design-circle-color']) {
      result.circleColor = formatColorFormat({
        colorType: settings['linear-progress-design-circle-color'] as any,
        colorSolid: settings['linear-progress-design-circle-color-solid'] as any,
        colorStartGradient: settings['linear-progress-design-circle-color-start-gradient'] as any,
        colorEndGradient: settings['linear-progress-design-circle-color-end-gradient'] as any,
        colorGradientDirection: settings['linear-progress-design-circle-color-gradient-direction'] as any,
      })
    }

    return result
  }

  /**
   * parse AI linear progress input to linear progress component settings
   */
  parse(input: AiLinearProgressInput): ILinearProgressComponentSettings {
    const result = deepMerge(LINEAR_PROGRESS_DEFAULT_TEMPLATE, {})

    // Data - Value
    if (input.value !== undefined) {
      result['linear-progress-data-value'] = {
        flag: 2048, // CustomText flag
        id: uuidv4(),
        value: String(input.value),
      }
    }

    // Data - Max Value
    if (input.maxValue !== undefined) {
      result['linear-progress-data-max-value'] = {
        flag: 2048, // CustomText flag
        id: uuidv4(),
        value: String(input.maxValue),
      }
    }

    // Data - Title
    if (input.progressTitle !== undefined) {
      result['linear-progress-data-title'] = parseToVariableText(input.progressTitle)
    }

    // Design - Width
    if (input.width) {
      result['linear-progress-design-width'] = parseWidthFormat(input.width)
    }

    // Design - Height
    if (input.height !== undefined) {
      if (typeof input.height === 'string') {
        // Parse responsive format if string
        result['linear-progress-design-height'] = parseResponsiveFormat(input.height, (val) => {
          // Extract number from string like "10px" -> 10
          const numMatch = val.match(/(\d+)/)
          return numMatch ? [parseInt(numMatch[1])] : [10]
        })
      } else {
        // Single number value
        result['linear-progress-design-height'] = {
          phone: [input.height],
          'phone-landscape': [input.height],
          tablet: [input.height],
          'tablet-landscape': [input.height],
          desktop: [input.height],
          tv: [input.height],
        }
      }
    }

    // Design - Alignment
    if (input.alignment) {
      // Linear progress alignment is a simple string, not array
      const alignmentMap: Record<string, string> = {
        left: 'left',
        center: 'center',
        right: 'right',
        parent: 'parent',
      }
      const alignment = alignmentMap[input.alignment] || 'center'
      result['linear-progress-design-alignment'] = {
        phone: alignment,
        'phone-landscape': alignment,
        tablet: alignment,
        'tablet-landscape': alignment,
        desktop: alignment,
        tv: alignment,
      }
    }

    // Design - Heading Alignment
    if (input.headingAlignment) {
      // Heading alignment is a simple value, not ResponsiveValue
      const alignmentMap: Record<string, string> = {
        left: 'left',
        center: 'center',
        right: 'right',
        parent: 'parent',
      }
      result['linear-progress-design-heading-alignment'] = alignmentMap[input.headingAlignment] || 'center'
    }

    // Design - Margin
    if (input.margin) {
      result['linear-progress-design-margin'] = parseSpacingFormat(input.margin)
    }

    // Design - Title Margin
    if (input.titleMargin) {
      result['linear-progress-design-title-margin'] = parseSpacingFormat(input.titleMargin)
    }

    // Design - Value Margin
    if (input.valueMargin) {
      // Value margin is SpacingInputValue (not ResponsiveValue)
      result['linear-progress-design-value-margin'] = parseSpacingFormat(input.valueMargin).phone
    }

    // Design - Typography
    if (input.typography) {
      result['linear-progress-design-typography'] = parseTypographyFormat(input.typography)
    }

    // Design - Heading Typography
    if (input.headingTypography) {
      result['linear-progress-design-heading-typography'] = parseTypographyFormat(input.headingTypography)
    }

    // Design - Value Typography
    if (input.valueTypography) {
      result['linear-progress-design-value-typography'] = parseTypographyFormat(input.valueTypography)
    }

    // Design - Track Color
    if (input.trackColor) {
      const colorParsed = parseColorFormat(input.trackColor)
      result['linear-progress-design-track-color'] = colorParsed.colorType
      result['linear-progress-design-track-color-solid'] = colorParsed.colorSolid
      result['linear-progress-design-track-color-start-gradient'] = colorParsed.colorStartGradient
      result['linear-progress-design-track-color-end-gradient'] = colorParsed.colorEndGradient
      result['linear-progress-design-track-color-gradient-direction'] = colorParsed.colorGradientDirection
    }

    // Design - Value Color
    if (input.valueColor) {
      const colorParsed = parseColorFormat(input.valueColor)
      result['linear-progress-design-value-color'] = colorParsed.colorType
      result['linear-progress-design-value-color-solid'] = colorParsed.colorSolid
      result['linear-progress-design-value-color-start-gradient'] = colorParsed.colorStartGradient
      result['linear-progress-design-value-color-end-gradient'] = colorParsed.colorEndGradient
      result['linear-progress-design-value-color-gradient-direction'] = colorParsed.colorGradientDirection
    }

    // Design - Circle Color
    if (input.circleColor) {
      const colorParsed = parseColorFormat(input.circleColor)
      result['linear-progress-design-circle-color'] = colorParsed.colorType
      result['linear-progress-design-circle-color-solid'] = colorParsed.colorSolid
      result['linear-progress-design-circle-color-start-gradient'] = colorParsed.colorStartGradient
      result['linear-progress-design-circle-color-end-gradient'] = colorParsed.colorEndGradient
      result['linear-progress-design-circle-color-gradient-direction'] = colorParsed.colorGradientDirection
    }

    return result
  }
}
